// Policies.js - Attendance Policies Management
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Policies = () => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState(null);

    const [formData, setFormData] = useState({
        policy_name: '',
        policy_description: '',
        attendance_mode: 'SHIFT',
        allow_extra_work_full_day: 1,
        deduct_break: 1,
        missing_in_rule: 'ASSUME_SHIFT',
        missing_out_rule: 'ASSUME_SHIFT',
        enable_sandwich_rule: 0,
        enable_overtime: 1,
        min_ot_minutes: 30,
        sandwich_rule_days: 1,
        late_marking_minutes: 15,
        early_going_minutes: 15,
        half_day_hours: 4.00,
        full_day_hours: 8.00,
        is_active: 1
    });

    const [validationErrors, setValidationErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filterActive, setFilterActive] = useState('all');

    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                const policyPermissions = response.data.permissions.find(
                    p => p.module_name === 'AttendancePolicies' || p.module_name === 'Policies'
                );

                if (policyPermissions) {
                    setUserPermissions({
                        can_view: Boolean(policyPermissions.can_view),
                        can_add: Boolean(policyPermissions.can_add),
                        can_edit: Boolean(policyPermissions.can_edit),
                        can_delete: Boolean(policyPermissions.can_delete),
                        can_export: Boolean(policyPermissions.can_export),
                        loaded: true
                    });
                } else {
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_export: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_export: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    const fetchPolicies = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}policy/get_policies.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setPolicies(response.data.policies || []);
                setError('');
            } else {
                setPolicies([]);
                const errorMsg = response.data?.error || 'Failed to fetch policies';
                setError(errorMsg);
            }
        } catch (error) {
            setPolicies([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch policies';
            setError(errorMsg);
            console.error('Error fetching policies:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchUserPermissions();
            await fetchPolicies();
        };

        initializeData();
    }, [fetchUserPermissions, fetchPolicies]);

    const canViewPolicies = () => userPermissions.can_view;
    const canAddPolicy = () => userPermissions.can_add;
    const canEditPolicy = () => userPermissions.can_edit;
    const canDeletePolicy = () => userPermissions.can_delete;
    const canExportPolicies = () => userPermissions.can_export;

    const handleAdd = () => {
        if (!canAddPolicy()) {
            alert('You do not have permission to add policies');
            return;
        }
        setFormData({
            policy_name: '',
            policy_description: '',
            attendance_mode: 'SHIFT',
            allow_extra_work_full_day: 1,
            deduct_break: 1,
            missing_in_rule: 'ASSUME_SHIFT',
            missing_out_rule: 'ASSUME_SHIFT',
            enable_sandwich_rule: 0,
            enable_overtime: 1,
            min_ot_minutes: 30,
            sandwich_rule_days: 1,
            late_marking_minutes: 15,
            early_going_minutes: 15,
            half_day_hours: 4.00,
            full_day_hours: 8.00,
            is_active: 1
        });
        setValidationErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (policy) => {
        if (!canEditPolicy()) {
            alert('You do not have permission to edit policies');
            return;
        }
        setSelectedPolicy(policy);
        setFormData({
            policy_name: policy.policy_name,
            policy_description: policy.policy_description || '',
            attendance_mode: policy.attendance_mode || 'SHIFT',
            allow_extra_work_full_day: policy.allow_extra_work_full_day ? 1 : 0,
            deduct_break: policy.deduct_break ? 1 : 0,
            missing_in_rule: policy.missing_in_rule || 'ASSUME_SHIFT',
            missing_out_rule: policy.missing_out_rule || 'ASSUME_SHIFT',
            enable_sandwich_rule: policy.enable_sandwich_rule ? 1 : 0,
            enable_overtime: policy.enable_overtime ? 1 : 0,
            min_ot_minutes: parseInt(policy.min_ot_minutes) || 30,
            sandwich_rule_days: parseInt(policy.sandwich_rule_days) || 1,
            late_marking_minutes: parseInt(policy.late_marking_minutes) || 15,
            early_going_minutes: parseInt(policy.early_going_minutes) || 15,
            half_day_hours: parseFloat(policy.half_day_hours) || 4.00,
            full_day_hours: parseFloat(policy.full_day_hours) || 8.00,
            is_active: policy.is_active ? 1 : 0
        });
        setValidationErrors({});
        setShowEditModal(true);
    };

    const handleView = (policy) => {
        setSelectedPolicy(policy);
        setShowViewModal(true);
    };

    const handleDelete = (policy) => {
        if (!canDeletePolicy()) {
            alert('You do not have permission to delete policies');
            return;
        }
        setSelectedPolicy(policy);
        setShowDeleteModal(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        let processedValue = value;
        if (type === 'number') {
            processedValue = value === '' ? '' : parseFloat(value);
        } else if (type === 'checkbox') {
            processedValue = checked ? 1 : 0;
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.policy_name.trim()) {
            errors.policy_name = 'Policy name is required';
        } else if (formData.policy_name.length > 100) {
            errors.policy_name = 'Policy name cannot exceed 100 characters';
        }

        if (formData.min_ot_minutes < 0 || formData.min_ot_minutes > 480) {
            errors.min_ot_minutes = 'Minimum OT minutes must be between 0 and 480';
        }

        if (formData.sandwich_rule_days < 0 || formData.sandwich_rule_days > 7) {
            errors.sandwich_rule_days = 'Sandwich rule days must be between 0 and 7';
        }

        if (formData.late_marking_minutes < 0 || formData.late_marking_minutes > 240) {
            errors.late_marking_minutes = 'Late marking minutes must be between 0 and 240';
        }

        if (formData.early_going_minutes < 0 || formData.early_going_minutes > 240) {
            errors.early_going_minutes = 'Early going minutes must be between 0 and 240';
        }

        if (formData.half_day_hours < 0 || formData.half_day_hours > 24) {
            errors.half_day_hours = 'Half day hours must be between 0 and 24';
        }

        if (formData.full_day_hours < formData.half_day_hours || formData.full_day_hours > 24) {
            errors.full_day_hours = 'Full day hours must be between half day hours and 24';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}policy/add_policy.php`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPolicies();
                setShowAddModal(false);
                alert('Policy added successfully');
            } else {
                setError(response.data.error || 'Failed to add policy');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to add policy');
            console.error('Error adding policy:', error);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}policy/update_policy.php`,
                {
                    policy_id: selectedPolicy.policy_id,
                    ...formData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPolicies();
                setShowEditModal(false);
                alert('Policy updated successfully');
            } else {
                setError(response.data.error || 'Failed to update policy');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update policy');
            console.error('Error updating policy:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}policy/delete_policy.php`,
                { policy_id: selectedPolicy.policy_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPolicies();
                setShowDeleteModal(false);
                alert('Policy deleted successfully');
            } else {
                setError(response.data.error || 'Failed to delete policy');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete policy');
            console.error('Error deleting policy:', error);
        }
    };

    const togglePolicyStatus = async (policyId, currentStatus) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}policy/toggle_policy_status.php`,
                {
                    policy_id: policyId,
                    is_active: currentStatus ? 0 : 1
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPolicies();
                alert('Policy status updated successfully');
            } else {
                setError(response.data.error || 'Failed to update policy status');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update policy status');
            console.error('Error toggling policy status:', error);
        }
    };

    const getAttendanceModeLabel = (mode) => {
        const labels = {
            'SHIFT': 'Shift Based',
            'ACTUAL': 'Actual Hours',
            'HYBRID': 'Hybrid'
        };
        return labels[mode] || mode;
    };

    const getRuleLabel = (rule) => {
        const labels = {
            'ASSUME_SHIFT': 'Assume Shift Time',
            'HALF_DAY': 'Mark as Half Day',
            'ABSENT': 'Mark as Absent'
        };
        return labels[rule] || rule;
    };

    const getBooleanLabel = (value) => {
        return value ? 'Yes' : 'No';
    };

    const getStatusBadge = (isActive) => {
        return isActive ?
            <span className="badge bg-success">Active</span> :
            <span className="badge bg-danger">Inactive</span>;
    };

    const getAttendanceModeColor = (mode) => {
        const colors = {
            'SHIFT': 'primary',
            'ACTUAL': 'success',
            'HYBRID': 'warning'
        };
        return colors[mode] || 'secondary';
    };

    const filteredPolicies = policies.filter(policy => {
        const matchesSearch =
            policy.policy_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            policy.policy_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            policy.attendance_mode?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter =
            filterActive === 'all' ||
            (filterActive === 'active' && policy.is_active) ||
            (filterActive === 'inactive' && !policy.is_active);

        return matchesSearch && matchesFilter;
    });

    const sortedPolicies = [...filteredPolicies].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.policy_name.localeCompare(b.policy_name);
            case 'mode':
                return a.attendance_mode.localeCompare(b.attendance_mode);
            case 'created':
                return new Date(b.created_at) - new Date(a.created_at);
            case 'status':
                return (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0);
            default:
                return a.policy_id - b.policy_id;
        }
    });

    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading permissions...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!canViewPolicies()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view attendance policies.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/admin-panel')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-shield-check me-2 text-primary"></i>
                                                Attendance Policies
                                                {!canAddPolicy() && !canEditPolicy() && !canDeletePolicy() && (
                                                    <span className="badge bg-warning ms-2">View Only</span>
                                                )}
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Manage attendance rules and regulations
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/admin-panel')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Dashboard
                                        </button>
                                        {canAddPolicy() && (
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Policy
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/admin-panel')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/employees')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Employees
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/shifts')}
                                    >
                                        <i className="bi bi-clock-history me-2"></i>
                                        Shifts
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/holidays')}
                                    >
                                        <i className="bi bi-calendar-event me-2"></i>
                                        Holidays
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i className="bi bi-shield-check me-2"></i>
                                        Policies
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/Menu')}
                                    >
                                        <i className="bi bi-menu-button me-2"></i>
                                        Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchPolicies}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filter Bar */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-3">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search policies..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterActive}
                                        onChange={(e) => setFilterActive(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active Only</option>
                                        <option value="inactive">Inactive Only</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="name">Sort by Name</option>
                                        <option value="mode">Sort by Mode</option>
                                        <option value="status">Sort by Status</option>
                                        <option value="created">Sort by Created Date</option>
                                    </select>
                                </div>
                                <div className="col-md-5 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={fetchPolicies}
                                            title="Refresh"
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                        {canExportPolicies() && (
                                            <button
                                                className="btn btn-outline-success me-2"
                                                onClick={() => alert('Export functionality coming soon!')}
                                                title="Export Policies"
                                            >
                                                <i className="bi bi-download"></i>
                                            </button>
                                        )}
                                        {canAddPolicy() && (
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Policy
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Policies Grid */}
            <div className="row">
                {sortedPolicies.length === 0 ? (
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-check display-1 text-muted"></i>
                                <h4 className="mt-3 text-muted">No policies found</h4>
                                <p className="text-muted">
                                    {policies.length === 0
                                        ? "No attendance policies available. Add your first policy."
                                        : "No policies match your search criteria."
                                    }
                                </p>
                                {policies.length === 0 && canAddPolicy() && (
                                    <button
                                        className="btn btn-primary text-white mt-2"
                                        onClick={handleAdd}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add First Policy
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    sortedPolicies.map((policy) => (
                        <div className="col-md-6 col-lg-4 mb-4" key={policy.policy_id}>
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="card-title mb-1">
                                                {policy.policy_name}
                                                <span className="badge bg-light text-dark border ms-2">
                                                    ID: {policy.policy_id}
                                                </span>
                                            </h5>
                                            <span className={`badge bg-${getAttendanceModeColor(policy.attendance_mode)}`}>
                                                {getAttendanceModeLabel(policy.attendance_mode)}
                                            </span>
                                        </div>
                                        <div>
                                            {getStatusBadge(policy.is_active)}
                                        </div>
                                    </div>

                                    {policy.policy_description && (
                                        <p className="card-text text-muted small mb-3">
                                            {policy.policy_description.length > 100
                                                ? `${policy.policy_description.substring(0, 100)}...`
                                                : policy.policy_description
                                            }
                                        </p>
                                    )}

                                    <div className="mb-3">
                                        <div className="row small g-2 mb-2">
                                            <div className="col-6">
                                                <span className="text-muted">Missing In:</span>
                                                <br />
                                                <strong>{getRuleLabel(policy.missing_in_rule)}</strong>
                                            </div>
                                            <div className="col-6">
                                                <span className="text-muted">Missing Out:</span>
                                                <br />
                                                <strong>{getRuleLabel(policy.missing_out_rule)}</strong>
                                            </div>
                                        </div>
                                        <div className="row small g-2 mb-2">
                                            <div className="col-6">
                                                <span className="text-muted">OT Enabled:</span>
                                                <br />
                                                <strong>{getBooleanLabel(policy.enable_overtime)}</strong>
                                            </div>
                                            <div className="col-6">
                                                <span className="text-muted">Min OT:</span>
                                                <br />
                                                <strong>{policy.min_ot_minutes} min</strong>
                                            </div>
                                        </div>
                                        <div className="row small g-2">
                                            <div className="col-6">
                                                <span className="text-muted">Half Day:</span>
                                                <br />
                                                <strong>{policy.half_day_hours}h</strong>
                                            </div>
                                            <div className="col-6">
                                                <span className="text-muted">Full Day:</span>
                                                <br />
                                                <strong>{policy.full_day_hours}h</strong>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            Created: {new Date(policy.created_at).toLocaleDateString()}
                                        </small>
                                        <div className="btn-group">
                                            <button
                                                className="btn btn-outline-info btn-sm"
                                                onClick={() => handleView(policy)}
                                                title="View Details"
                                            >
                                                <i className="bi bi-eye"></i>
                                            </button>
                                            {canEditPolicy() && (
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => handleEdit(policy)}
                                                    title="Edit Policy"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                            )}
                                            {canDeletePolicy() && (
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleDelete(policy)}
                                                    title="Delete Policy"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            )}
                                            {canEditPolicy() && (
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => togglePolicyStatus(policy.policy_id, policy.is_active)}
                                                    title={policy.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    <i className={`bi bi-power ${policy.is_active ? 'text-success' : 'text-danger'}`}></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Stats Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="fw-bold text-primary fs-4">{policies.length}</div>
                                    <small className="text-muted">Total Policies</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {policies.filter(p => p.is_active).length}
                                    </div>
                                    <small className="text-muted">Active Policies</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-warning fs-4">
                                        {policies.filter(p => p.attendance_mode === 'SHIFT').length}
                                    </div>
                                    <small className="text-muted">Shift Based</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-info fs-4">
                                        {policies.filter(p => p.enable_overtime).length}
                                    </div>
                                    <small className="text-muted">OT Enabled</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={fetchPolicies}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Refresh Data
                                </button>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => navigate('/shifts')}
                                >
                                    <i className="bi bi-clock-history me-2"></i>
                                    Manage Shifts
                                </button>
                                <button
                                    className="btn btn-outline-warning"
                                    onClick={() => navigate('/holidays')}
                                >
                                    <i className="bi bi-calendar-event me-2"></i>
                                    Manage Holidays
                                </button>
                                {canAddPolicy() && (
                                    <button
                                        className="btn btn-primary text-white"
                                        onClick={handleAdd}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add New Policy
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Policy Modal */}
            {showAddModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add New Attendance Policy
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowAddModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleAddSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        {/* Basic Information */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="policy_name" className="form-label fw-medium">
                                                    Policy Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.policy_name ? 'is-invalid' : ''}`}
                                                    id="policy_name"
                                                    name="policy_name"
                                                    value={formData.policy_name}
                                                    onChange={handleChange}
                                                    placeholder="e.g., Standard Attendance Policy"
                                                    required
                                                />
                                                {validationErrors.policy_name && (
                                                    <div className="invalid-feedback">{validationErrors.policy_name}</div>
                                                )}
                                                <div className="form-text">Name of the attendance policy</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="policy_description" className="form-label fw-medium">
                                                    Description
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    id="policy_description"
                                                    name="policy_description"
                                                    value={formData.policy_description}
                                                    onChange={handleChange}
                                                    rows="3"
                                                    placeholder="Brief description of the policy..."
                                                ></textarea>
                                                <div className="form-text">Optional description of the policy</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="attendance_mode" className="form-label fw-medium">
                                                    Attendance Mode <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="attendance_mode"
                                                    name="attendance_mode"
                                                    value={formData.attendance_mode}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="SHIFT">Shift Based</option>
                                                    <option value="ACTUAL">Actual Hours</option>
                                                    <option value="HYBRID">Hybrid</option>
                                                </select>
                                                <div className="form-text">
                                                    SHIFT: Based on shift timings<br />
                                                    ACTUAL: Based on actual worked hours<br />
                                                    HYBRID: Combination of both
                                                </div>
                                            </div>
                                        </div>

                                        {/* Hours Configuration */}
                                        <div className="col-md-6">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="half_day_hours" className="form-label fw-medium">
                                                            Half Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.half_day_hours ? 'is-invalid' : ''}`}
                                                            id="half_day_hours"
                                                            name="half_day_hours"
                                                            value={formData.half_day_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="24"
                                                            step="0.5"
                                                        />
                                                        {validationErrors.half_day_hours && (
                                                            <div className="invalid-feedback">{validationErrors.half_day_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="full_day_hours" className="form-label fw-medium">
                                                            Full Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.full_day_hours ? 'is-invalid' : ''}`}
                                                            id="full_day_hours"
                                                            name="full_day_hours"
                                                            value={formData.full_day_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="24"
                                                            step="0.5"
                                                        />
                                                        {validationErrors.full_day_hours && (
                                                            <div className="invalid-feedback">{validationErrors.full_day_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="late_marking_minutes" className="form-label fw-medium">
                                                    Late Marking Allowance (minutes)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.late_marking_minutes ? 'is-invalid' : ''}`}
                                                    id="late_marking_minutes"
                                                    name="late_marking_minutes"
                                                    value={formData.late_marking_minutes}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="240"
                                                />
                                                {validationErrors.late_marking_minutes && (
                                                    <div className="invalid-feedback">{validationErrors.late_marking_minutes}</div>
                                                )}
                                                <div className="form-text">Allowed minutes for late marking</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="early_going_minutes" className="form-label fw-medium">
                                                    Early Going Allowance (minutes)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.early_going_minutes ? 'is-invalid' : ''}`}
                                                    id="early_going_minutes"
                                                    name="early_going_minutes"
                                                    value={formData.early_going_minutes}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="240"
                                                />
                                                {validationErrors.early_going_minutes && (
                                                    <div className="invalid-feedback">{validationErrors.early_going_minutes}</div>
                                                )}
                                                <div className="form-text">Allowed minutes for early leaving</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Missing Rules */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="missing_in_rule" className="form-label fw-medium">
                                                    Missing In-Punch Rule
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="missing_in_rule"
                                                    name="missing_in_rule"
                                                    value={formData.missing_in_rule}
                                                    onChange={handleChange}
                                                >
                                                    <option value="ASSUME_SHIFT">Assume Shift Start Time</option>
                                                    <option value="HALF_DAY">Mark as Half Day</option>
                                                    <option value="ABSENT">Mark as Absent</option>
                                                </select>
                                                <div className="form-text">Action when employee forgets to punch in</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="missing_out_rule" className="form-label fw-medium">
                                                    Missing Out-Punch Rule
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="missing_out_rule"
                                                    name="missing_out_rule"
                                                    value={formData.missing_out_rule}
                                                    onChange={handleChange}
                                                >
                                                    <option value="ASSUME_SHIFT">Assume Shift End Time</option>
                                                    <option value="HALF_DAY">Mark as Half Day</option>
                                                    <option value="ABSENT">Mark as Absent</option>
                                                </select>
                                                <div className="form-text">Action when employee forgets to punch out</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Overtime Settings */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="enable_overtime"
                                                        name="enable_overtime"
                                                        checked={formData.enable_overtime === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="enable_overtime">
                                                        Enable Overtime
                                                    </label>
                                                </div>
                                            </div>

                                            {formData.enable_overtime === 1 && (
                                                <div className="mb-3">
                                                    <label htmlFor="min_ot_minutes" className="form-label fw-medium">
                                                        Minimum Overtime Minutes
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${validationErrors.min_ot_minutes ? 'is-invalid' : ''}`}
                                                        id="min_ot_minutes"
                                                        name="min_ot_minutes"
                                                        value={formData.min_ot_minutes}
                                                        onChange={handleChange}
                                                        min="0"
                                                        max="480"
                                                    />
                                                    {validationErrors.min_ot_minutes && (
                                                        <div className="invalid-feedback">{validationErrors.min_ot_minutes}</div>
                                                    )}
                                                    <div className="form-text">Minimum minutes to count as overtime</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="enable_sandwich_rule"
                                                        name="enable_sandwich_rule"
                                                        checked={formData.enable_sandwich_rule === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="enable_sandwich_rule">
                                                        Enable Sandwich Rule
                                                    </label>
                                                </div>
                                            </div>

                                            {formData.enable_sandwich_rule === 1 && (
                                                <div className="mb-3">
                                                    <label htmlFor="sandwich_rule_days" className="form-label fw-medium">
                                                        Sandwich Rule Days
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${validationErrors.sandwich_rule_days ? 'is-invalid' : ''}`}
                                                        id="sandwich_rule_days"
                                                        name="sandwich_rule_days"
                                                        value={formData.sandwich_rule_days}
                                                        onChange={handleChange}
                                                        min="0"
                                                        max="7"
                                                    />
                                                    {validationErrors.sandwich_rule_days && (
                                                        <div className="invalid-feedback">{validationErrors.sandwich_rule_days}</div>
                                                    )}
                                                    <div className="form-text">Days between leaves to apply sandwich rule</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Other Settings */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="allow_extra_work_full_day"
                                                        name="allow_extra_work_full_day"
                                                        checked={formData.allow_extra_work_full_day === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="allow_extra_work_full_day">
                                                        Allow Extra Work for Full Day
                                                    </label>
                                                </div>
                                                <div className="form-text">Allow working extra hours to complete full day</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="deduct_break"
                                                        name="deduct_break"
                                                        checked={formData.deduct_break === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="deduct_break">
                                                        Deduct Break Time
                                                    </label>
                                                </div>
                                                <div className="form-text">Deduct break time from working hours</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="is_active"
                                                        name="is_active"
                                                        checked={formData.is_active === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="is_active">
                                                        Active Policy
                                                    </label>
                                                </div>
                                                <div className="form-text">Only active policies can be assigned</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowAddModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Policy
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Policy Modal */}
            {showEditModal && selectedPolicy && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Edit Policy: {selectedPolicy.policy_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        {/* Basic Information */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="policy_name" className="form-label fw-medium">
                                                    Policy Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.policy_name ? 'is-invalid' : ''}`}
                                                    id="policy_name"
                                                    name="policy_name"
                                                    value={formData.policy_name}
                                                    onChange={handleChange}
                                                    placeholder="e.g., Standard Attendance Policy"
                                                    required
                                                />
                                                {validationErrors.policy_name && (
                                                    <div className="invalid-feedback">{validationErrors.policy_name}</div>
                                                )}
                                                <div className="form-text">Name of the attendance policy</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="policy_description" className="form-label fw-medium">
                                                    Description
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    id="policy_description"
                                                    name="policy_description"
                                                    value={formData.policy_description}
                                                    onChange={handleChange}
                                                    rows="3"
                                                    placeholder="Brief description of the policy..."
                                                ></textarea>
                                                <div className="form-text">Optional description of the policy</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="attendance_mode" className="form-label fw-medium">
                                                    Attendance Mode <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="attendance_mode"
                                                    name="attendance_mode"
                                                    value={formData.attendance_mode}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="SHIFT">Shift Based</option>
                                                    <option value="ACTUAL">Actual Hours</option>
                                                    <option value="HYBRID">Hybrid</option>
                                                </select>
                                                <div className="form-text">
                                                    SHIFT: Based on shift timings<br />
                                                    ACTUAL: Based on actual worked hours<br />
                                                    HYBRID: Combination of both
                                                </div>
                                            </div>
                                        </div>

                                        {/* Hours Configuration */}
                                        <div className="col-md-6">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="half_day_hours" className="form-label fw-medium">
                                                            Half Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.half_day_hours ? 'is-invalid' : ''}`}
                                                            id="half_day_hours"
                                                            name="half_day_hours"
                                                            value={formData.half_day_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="24"
                                                            step="0.5"
                                                        />
                                                        {validationErrors.half_day_hours && (
                                                            <div className="invalid-feedback">{validationErrors.half_day_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="full_day_hours" className="form-label fw-medium">
                                                            Full Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.full_day_hours ? 'is-invalid' : ''}`}
                                                            id="full_day_hours"
                                                            name="full_day_hours"
                                                            value={formData.full_day_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="24"
                                                            step="0.5"
                                                        />
                                                        {validationErrors.full_day_hours && (
                                                            <div className="invalid-feedback">{validationErrors.full_day_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="late_marking_minutes" className="form-label fw-medium">
                                                    Late Marking Allowance (minutes)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.late_marking_minutes ? 'is-invalid' : ''}`}
                                                    id="late_marking_minutes"
                                                    name="late_marking_minutes"
                                                    value={formData.late_marking_minutes}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="240"
                                                />
                                                {validationErrors.late_marking_minutes && (
                                                    <div className="invalid-feedback">{validationErrors.late_marking_minutes}</div>
                                                )}
                                                <div className="form-text">Allowed minutes for late marking</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="early_going_minutes" className="form-label fw-medium">
                                                    Early Going Allowance (minutes)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.early_going_minutes ? 'is-invalid' : ''}`}
                                                    id="early_going_minutes"
                                                    name="early_going_minutes"
                                                    value={formData.early_going_minutes}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="240"
                                                />
                                                {validationErrors.early_going_minutes && (
                                                    <div className="invalid-feedback">{validationErrors.early_going_minutes}</div>
                                                )}
                                                <div className="form-text">Allowed minutes for early leaving</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Missing Rules */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="missing_in_rule" className="form-label fw-medium">
                                                    Missing In-Punch Rule
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="missing_in_rule"
                                                    name="missing_in_rule"
                                                    value={formData.missing_in_rule}
                                                    onChange={handleChange}
                                                >
                                                    <option value="ASSUME_SHIFT">Assume Shift Start Time</option>
                                                    <option value="HALF_DAY">Mark as Half Day</option>
                                                    <option value="ABSENT">Mark as Absent</option>
                                                </select>
                                                <div className="form-text">Action when employee forgets to punch in</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="missing_out_rule" className="form-label fw-medium">
                                                    Missing Out-Punch Rule
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="missing_out_rule"
                                                    name="missing_out_rule"
                                                    value={formData.missing_out_rule}
                                                    onChange={handleChange}
                                                >
                                                    <option value="ASSUME_SHIFT">Assume Shift End Time</option>
                                                    <option value="HALF_DAY">Mark as Half Day</option>
                                                    <option value="ABSENT">Mark as Absent</option>
                                                </select>
                                                <div className="form-text">Action when employee forgets to punch out</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Overtime Settings */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="enable_overtime"
                                                        name="enable_overtime"
                                                        checked={formData.enable_overtime === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="enable_overtime">
                                                        Enable Overtime
                                                    </label>
                                                </div>
                                            </div>

                                            {formData.enable_overtime === 1 && (
                                                <div className="mb-3">
                                                    <label htmlFor="min_ot_minutes" className="form-label fw-medium">
                                                        Minimum Overtime Minutes
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${validationErrors.min_ot_minutes ? 'is-invalid' : ''}`}
                                                        id="min_ot_minutes"
                                                        name="min_ot_minutes"
                                                        value={formData.min_ot_minutes}
                                                        onChange={handleChange}
                                                        min="0"
                                                        max="480"
                                                    />
                                                    {validationErrors.min_ot_minutes && (
                                                        <div className="invalid-feedback">{validationErrors.min_ot_minutes}</div>
                                                    )}
                                                    <div className="form-text">Minimum minutes to count as overtime</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="enable_sandwich_rule"
                                                        name="enable_sandwich_rule"
                                                        checked={formData.enable_sandwich_rule === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="enable_sandwich_rule">
                                                        Enable Sandwich Rule
                                                    </label>
                                                </div>
                                            </div>

                                            {formData.enable_sandwich_rule === 1 && (
                                                <div className="mb-3">
                                                    <label htmlFor="sandwich_rule_days" className="form-label fw-medium">
                                                        Sandwich Rule Days
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${validationErrors.sandwich_rule_days ? 'is-invalid' : ''}`}
                                                        id="sandwich_rule_days"
                                                        name="sandwich_rule_days"
                                                        value={formData.sandwich_rule_days}
                                                        onChange={handleChange}
                                                        min="0"
                                                        max="7"
                                                    />
                                                    {validationErrors.sandwich_rule_days && (
                                                        <div className="invalid-feedback">{validationErrors.sandwich_rule_days}</div>
                                                    )}
                                                    <div className="form-text">Days between leaves to apply sandwich rule</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Other Settings */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="allow_extra_work_full_day"
                                                        name="allow_extra_work_full_day"
                                                        checked={formData.allow_extra_work_full_day === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="allow_extra_work_full_day">
                                                        Allow Extra Work for Full Day
                                                    </label>
                                                </div>
                                                <div className="form-text">Allow working extra hours to complete full day</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="deduct_break"
                                                        name="deduct_break"
                                                        checked={formData.deduct_break === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="deduct_break">
                                                        Deduct Break Time
                                                    </label>
                                                </div>
                                                <div className="form-text">Deduct break time from working hours</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="is_active"
                                                        name="is_active"
                                                        checked={formData.is_active === 1}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor="is_active">
                                                        Active Policy
                                                    </label>
                                                </div>
                                                <div className="form-text">Only active policies can be assigned</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Update Policy
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* View Policy Modal */}
            {showViewModal && selectedPolicy && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-eye me-2"></i>
                                    Policy Details: {selectedPolicy.policy_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="40%">Policy Name:</th>
                                                    <td>{selectedPolicy.policy_name}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Description:</th>
                                                    <td>{selectedPolicy.policy_description || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Attendance Mode:</th>
                                                    <td>
                                                        <span className={`badge bg-${getAttendanceModeColor(selectedPolicy.attendance_mode)}`}>
                                                            {getAttendanceModeLabel(selectedPolicy.attendance_mode)}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Status:</th>
                                                    <td>{getStatusBadge(selectedPolicy.is_active)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="40%">Half Day Hours:</th>
                                                    <td>{selectedPolicy.half_day_hours} hours</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Full Day Hours:</th>
                                                    <td>{selectedPolicy.full_day_hours} hours</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Late Allowance:</th>
                                                    <td>{selectedPolicy.late_marking_minutes} minutes</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Early Allowance:</th>
                                                    <td>{selectedPolicy.early_going_minutes} minutes</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <div className="card border-0 bg-light">
                                            <div className="card-body">
                                                <h6 className="fw-medium mb-3">
                                                    <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                                                    Missing Punch Rules
                                                </h6>
                                                <div className="row small">
                                                    <div className="col-6">
                                                        <strong>Missing In:</strong>
                                                        <div>{getRuleLabel(selectedPolicy.missing_in_rule)}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <strong>Missing Out:</strong>
                                                        <div>{getRuleLabel(selectedPolicy.missing_out_rule)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card border-0 bg-light">
                                            <div className="card-body">
                                                <h6 className="fw-medium mb-3">
                                                    <i className="bi bi-clock-history me-2 text-success"></i>
                                                    Overtime Settings
                                                </h6>
                                                <div className="row small">
                                                    <div className="col-6">
                                                        <strong>Overtime:</strong>
                                                        <div>{getBooleanLabel(selectedPolicy.enable_overtime)}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <strong>Min OT:</strong>
                                                        <div>{selectedPolicy.min_ot_minutes} minutes</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <div className="card border-0 bg-light">
                                            <div className="card-body">
                                                <h6 className="fw-medium mb-3">
                                                    <i className="bi bi-toggle-on me-2 text-primary"></i>
                                                    Other Settings
                                                </h6>
                                                <div className="row small">
                                                    <div className="col-6">
                                                        <strong>Extra Work:</strong>
                                                        <div>{getBooleanLabel(selectedPolicy.allow_extra_work_full_day)}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <strong>Deduct Break:</strong>
                                                        <div>{getBooleanLabel(selectedPolicy.deduct_break)}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <strong>Sandwich Rule:</strong>
                                                        <div>{getBooleanLabel(selectedPolicy.enable_sandwich_rule)}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <strong>Sandwich Days:</strong>
                                                        <div>{selectedPolicy.sandwich_rule_days} days</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card border-0 bg-light">
                                            <div className="card-body">
                                                <h6 className="fw-medium mb-3">
                                                    <i className="bi bi-calendar me-2 text-info"></i>
                                                    Dates
                                                </h6>
                                                <div className="row small">
                                                    <div className="col-12">
                                                        <strong>Created:</strong>
                                                        <div>{new Date(selectedPolicy.created_at).toLocaleString()}</div>
                                                    </div>
                                                    <div className="col-12 mt-2">
                                                        <strong>Updated:</strong>
                                                        <div>{new Date(selectedPolicy.updated_at).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedPolicy && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>

                                <p>Are you sure you want to delete the following policy?</p>

                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h6 className="fw-bold">{selectedPolicy.policy_name}</h6>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Mode</small>
                                                <strong>{getAttendanceModeLabel(selectedPolicy.attendance_mode)}</strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Status</small>
                                                <strong>{selectedPolicy.is_active ? 'Active' : 'Inactive'}</strong>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Half Day</small>
                                                <strong>{selectedPolicy.half_day_hours} hours</strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Full Day</small>
                                                <strong>{selectedPolicy.full_day_hours} hours</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-danger mt-3" role="alert">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <strong>Note:</strong> Deleting this policy may affect assigned shifts and employees.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light border-secondary-subtle"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    Delete Policy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Policies;